import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

export interface CheckinMailItem {
  id: number;
  patient_id: number;
  clinic_id: number;
  email: string;
  is_checkin_alert: boolean;
  is_checkout_alert: boolean;
  is_status: boolean;
  remarks: string;
  add_sentence: string;
  created_at?: string;
  updated_at?: string;
}

export interface CheckinMailListResponse {
  success: boolean;
  data: {
    patient: {
      id: number;
      serial_number: number | string;
      current_patient_information?: { name?: string; name_kana?: string };
    };
    checkin_mails: CheckinMailItem[];
  };
}

/**
 * Fetch CI通知 list for a patient.
 * API: GET /api/v1/clinic/{site_type}/{clinic_number}/patients/{patientId}/checkin-mails
 */
export async function fetchCheckinMailList(
  siteType: string,
  clinicNumber: string,
  patientId: string,
): Promise<{ patient: CheckinMailListResponse['data']['patient']; list: CheckinMailItem[] }> {
  const response = await apiClient.get<CheckinMailListResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/checkin-mails`),
  );
  const data = response.data.data;
  return {
    patient: data.patient,
    list: data.checkin_mails ?? [],
  };
}

export interface CheckinMailDetailResponse {
  success: boolean;
  data: CheckinMailItem;
}

export async function fetchCheckinMailOne(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: string,
): Promise<CheckinMailItem> {
  const response = await apiClient.get<CheckinMailDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/checkin-mails/${id}`),
  );
  return response.data.data;
}

export interface CheckinMailPayload {
  email: string;
  is_status?: boolean;
  is_checkin_alert?: boolean;
  is_checkout_alert?: boolean;
  add_sentence?: string;
  remarks?: string;
}

export async function createCheckinMail(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  payload: CheckinMailPayload,
): Promise<CheckinMailItem> {
  const response = await apiClient.post<CheckinMailDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/checkin-mails`),
    payload,
  );
  return response.data.data;
}

export async function updateCheckinMail(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: number,
  payload: CheckinMailPayload,
): Promise<CheckinMailItem> {
  const response = await apiClient.put<CheckinMailDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/checkin-mails/${id}`),
    payload,
  );
  return response.data.data;
}

export async function deleteCheckinMail(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: number,
): Promise<void> {
  await apiClient.delete(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/checkin-mails/${id}`),
  );
}

